import type { Scope } from "@/api/usePolicies.types"
import {
  testIsValidCVE,
  testIsValidCWE,
  testIsValidGHSA
} from "@/domain/vulnerability"
import { camelToSnakeCase, trim } from "@/helpers/string.helpers"

import type { ResponseFromSelector } from "."

type SpecificScope = {
  name: keyof Scope | undefined
  value: string[] | string | null | undefined
}[]

export const getEmptyScope = () => ({ name: undefined, value: undefined })

export const getInitialValues = (
  response: ResponseFromSelector | undefined,
  isNewPolicy: boolean
) => {
  const { specificScopes, ...rest } =
    isNewPolicy || !response?.data?.[0]
      ? {
          scope: {} as Scope,
          specificScopes: [getEmptyScope()],
          currentConditions: [[], []],
          currentScope: "all"
        }
      : response.data[0]

  return {
    specificScopes: !specificScopes.length ? [getEmptyScope()] : specificScopes,
    ...rest
  }
}

export const getScopeForPayload = (values: {
  currentScope: "all" | unknown
  specificScopes: SpecificScope
}) => {
  const { currentScope, specificScopes } = values
  return currentScope === "all"
    ? {
        repository_name: null,
        organization_name: null,
        github_topics: null
      }
    : specificScopes?.reduce(
        (acc, { name, value }) => {
          if (!name || !value) return acc

          const nextValue = trim(value)

          acc[camelToSnakeCase(name)] = Array.isArray(nextValue)
            ? nextValue.join(",")
            : nextValue

          return acc
        },
        {} as Record<string, string>
      )
}

export const getConditionsForPayload = (values: {
  currentConditions: { key: string; selectedValue: unknown }[][]
}) =>
  (values.currentConditions || [])
    .filter((value) => !!value?.length)
    .map((value) =>
      value.reduce(
        (acc, condition) => {
          const { selectedValue } = condition

          acc[condition.key] = trim(selectedValue)

          return acc
        },
        {} as Record<string, unknown>
      )
    )

export const testIsValidID = (value: string) =>
  testIsValidCVE(value) || testIsValidGHSA(value)

export const ghsaValidator = (_: unknown, value: string[]) => {
  if (!value?.every(testIsValidGHSA)) return Promise.reject()

  return Promise.resolve()
}

// Validate the vulnerability ID to be either a CVE or GHSA ID
export const vulnerabilityIDValidator = (_: unknown, value: string[]) => {
  if (!value?.every((v) => testIsValidID(v))) return Promise.reject()

  return Promise.resolve()
}

export const cweIDValidator = (_: unknown, value: string[]) => {
  if (!value?.every((v) => testIsValidCWE(v))) return Promise.reject()

  return Promise.resolve()
}
